import React from 'react';
import { AuditLog, AUDIT_ACTION_LABELS, AUDIT_ACTION_COLORS, AUDIT_ENTITY_LABELS } from '../../types/audit';
import { EmptyState } from '../ui/Feedback';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';

interface AuditLogTableProps {
  logs: AuditLog[];
  isLoading?: boolean;
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
  onSelect?: (log: AuditLog) => void;
}

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export const AuditLogTable: React.FC<AuditLogTableProps> = ({
  logs, isLoading, page, totalPages, total, limit, onPageChange, onSelect,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-slate-800/50 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (logs.length === 0) {
    return <EmptyState message="No audit logs found" description="Adjust the filters to see activity records." />;
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto hidden md:block">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Time</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">User</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Action</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Entity</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Details</th>
              <th className="text-right py-3 px-3 font-semibold text-slate-400 w-16">View</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(log => (
              <tr key={log.log_id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                <td className="py-3 px-3 text-slate-400 whitespace-nowrap">{formatDateTime(log.timestamp)}</td>
                <td className="py-3 px-3 text-slate-200">{log.user_name || log.user_id || 'System'}</td>
                <td className="py-3 px-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${AUDIT_ACTION_COLORS[log.action]}`}>
                    {AUDIT_ACTION_LABELS[log.action]}
                  </span>
                </td>
                <td className="py-3 px-3">
                  <div className="text-slate-300">{AUDIT_ENTITY_LABELS[log.entity_type]}</div>
                  {log.entity_id && <div className="text-[10px] text-slate-500 font-mono">{log.entity_id}</div>}
                </td>
                <td className="py-3 px-3 text-slate-400 max-w-xs truncate">{log.description || '-'}</td>
                <td className="py-3 px-3 text-right">
                  <button onClick={() => onSelect?.(log)} className="p-1.5 rounded-lg text-slate-400 hover:text-sky-400 hover:bg-sky-500/10 transition-colors">
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-2">
        {logs.map(log => (
          <button
            key={log.log_id}
            onClick={() => onSelect?.(log)}
            className="w-full text-left bg-slate-800/40 border border-slate-700/50 rounded-lg p-3 hover:border-sky-500/40 transition-colors"
          >
            <div className="flex items-center justify-between gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${AUDIT_ACTION_COLORS[log.action]}`}>
                {AUDIT_ACTION_LABELS[log.action]}
              </span>
              <span className="text-[10px] text-slate-500">{formatDateTime(log.timestamp)}</span>
            </div>
            <div className="mt-2 text-xs text-slate-200">{log.user_name || log.user_id}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              {AUDIT_ENTITY_LABELS[log.entity_type]} {log.entity_id && <span className="font-mono">{log.entity_id}</span>}
            </div>
            {log.description && <div className="text-[11px] text-slate-400 mt-1 truncate">{log.description}</div>}
          </button>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page === 1} className="p-1.5 rounded-lg hover:bg-slate-800 disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2">{page} / {totalPages}</span>
            <button onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg hover:bg-slate-800 disabled:opacity-30">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
